import React from "react";
import { useCurrentFrame, interpolate, spring, useVideoConfig } from "remotion";
import { SANSKRIT_TERMS } from "../styles/theme";

interface AnimatedTextProps {
  text: string;
  startFrame: number;
  durationFrames: number;
  style?: "word-reveal" | "fade-in" | "highlight";
  fontSize?: number;
  color?: string;
  accentColor?: string;
  fontFamily?: string;
  textAlign?: "left" | "center" | "right";
  maxWidth?: number;
}

const sanskritSet = new Set(
  SANSKRIT_TERMS.map((t) => t.toLowerCase())
);

function isSanskritWord(word: string): boolean {
  const clean = word.replace(/[.,;:!?'"()]/g, "").toLowerCase();
  return sanskritSet.has(clean);
}

export const AnimatedText: React.FC<AnimatedTextProps> = ({
  text,
  startFrame,
  durationFrames,
  style = "word-reveal",
  fontSize = 38,
  color = "#FFFFFF",
  accentColor = "#F37021",
  fontFamily = "Poppins, sans-serif",
  textAlign = "center",
  maxWidth,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const words = text.split(/\s+/);
  const framesPerWord = Math.max(3, durationFrames / words.length);

  if (style === "fade-in") {
    const opacity = interpolate(
      frame,
      [startFrame, startFrame + 24],
      [0, 1],
      { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
    );

    return (
      <div
        style={{
          opacity,
          fontSize,
          color,
          fontFamily,
          textAlign,
          lineHeight: 1.5,
          fontWeight: 600,
          maxWidth,
        }}
      >
        {text}
      </div>
    );
  }

  if (style === "highlight") {
    // Word-by-word highlight: current word is accented, past words are white, future dimmed
    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: textAlign === "center" ? "center" : "flex-start",
          gap: "0.25em",
          maxWidth,
        }}
      >
        {words.map((word, i) => {
          const wordFrame = startFrame + i * framesPerWord;
          const isActive =
            frame >= wordFrame && frame < wordFrame + framesPerWord;
          const isPast = frame >= wordFrame + framesPerWord;
          const isSanskrit = isSanskritWord(word);

          let wordColor = `${color}99`; // future: dimmed
          if (isPast) wordColor = color;
          if (isActive) wordColor = accentColor;
          if (isSanskrit && (isPast || isActive)) wordColor = accentColor;

          const scale = isActive ? 1.05 : 1;

          return (
            <span
              key={i}
              style={{
                fontSize,
                color: wordColor,
                fontFamily,
                fontWeight: isSanskrit ? 700 : 600,
                fontStyle: isSanskrit ? "italic" : "normal",
                transform: `scale(${scale})`,
                transition: "color 0.1s, transform 0.1s",
                lineHeight: 1.5,
              }}
            >
              {word}
            </span>
          );
        })}
      </div>
    );
  }

  // Default: word-reveal
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: textAlign === "center" ? "center" : "flex-start",
        gap: "0.3em",
        maxWidth,
      }}
    >
      {words.map((word, i) => {
        const wordStart = startFrame + i * framesPerWord;
        const progress = spring({
          frame: frame - wordStart,
          fps,
          config: { damping: 20, stiffness: 120 },
        });

        const opacity = interpolate(progress, [0, 1], [0, 1]);
        const translateY = interpolate(progress, [0, 1], [14, 0]);
        const isSanskrit = isSanskritWord(word);

        return (
          <span
            key={i}
            style={{
              opacity,
              transform: `translateY(${translateY}px)`,
              fontSize,
              color: isSanskrit ? accentColor : color,
              fontFamily,
              fontWeight: isSanskrit ? 700 : 600,
              fontStyle: isSanskrit ? "italic" : "normal",
              lineHeight: 1.5,
            }}
          >
            {word}
          </span>
        );
      })}
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
